import http from "./httpService";
import {
  AcceptOfferFormType,
  CloneTaxCaseType,
  PurchaseType,
  RejectCaseType,
  SendOfferFormType,
  TaxCase,
  TaxCaseCreation,
  TaxCaseCreationBulk,
  TaxCreationRequestType,
  TaxListItem,
  TaxRecalculateRequestType,
  TradePriceCalcTaxType,
} from "../types/taxTypes";
import {ChangeClientType, IdType, LinkType, Vehicle,} from "../types/appTypes";
import {getApiQuery} from "./urlQueryService";
import {mediaApiEndpoint} from "./mediaService";
import getApiEndpoint from "./apiEndpoint";
import {PaginationedData} from "../types/apiTypes";
import {ToLeasingFormType} from "../types/leasingTypes";
import {CaseStatusUpdate} from "../types/casesCommonTypes";
import {latestVersion} from "../screens/forms/tax/calc/taxCalcService";
import {getTaxScenariosUrl} from "./taxScenarioService";

const apiEndpoint = `${getApiEndpoint()}/taxCase`;

export function taxCaseUrl(id: string | number) {
  return `${apiEndpoint}/${id}`;
}

export function taxFileUrl(id?: IdType) {
  return id ? `${taxCaseUrl(id)}/file` : mediaApiEndpoint;
}

export function taxActivitiesUrl(id: IdType) {
  return `${taxCaseUrl(id)}/activities`;
}

export function getSavedOfferUrl(id: IdType, optimizationMode: boolean) {
  return getTaxScenariosUrl(id, optimizationMode);
}

export function getTaxCalculation(
  id: IdType,
  optimizationMode: boolean,
  data: any
) {
  return http.post<TradePriceCalcTaxType>(
    `${taxCaseUrl(id)}/${optimizationMode ? "billingOptimization" : "preAssessment"
    }/calculate`,
    data
  );
}

export function getTaxRegCalculation(
  id: IdType,
  optimizationMode: boolean,
  data: any
) {
  return http.post<TradePriceCalcTaxType>(
    `${taxCaseUrl(id)}/${optimizationMode ? "billingOptimization" : "preAssessment"
    }/calculateRegistrationFee`,
    data
  );
}

export function updateTaxActivity(id: IdType, data: any) {
  return http.put(`${taxActivitiesUrl(id)}/${data?.id}`, data);
}

export function saveTaxActivity(id: IdType, data: any) {
  return http.post(taxActivitiesUrl(id), data);
}

export function deleteTaxActivity(id: IdType, deletedId: string) {
  return http.delete(`${taxActivitiesUrl(id)}/${deletedId}`);
}

export function taxLegalFileUrl(id?: IdType) {
  return id ? `${taxCaseUrl(id)}/legalFile` : mediaApiEndpoint;
}

export function taxCasesURL(query?: string) {
  return `${apiEndpoint}?${getApiQuery(query)}`;
}

export function getTaxCases(query?: string) {
  return http.get<PaginationedData<TaxListItem>>(
    `${apiEndpoint}?${getApiQuery(query)}`
  );
}

export function getTaxCase(id: string | number) {
  return http.get<TaxCase>(taxCaseUrl(id));
}

export function deleteTaxCase(id: IdType, doRefund: boolean) {
  const refundReason = doRefund ? 5 : -1;
  const url = `${taxCaseUrl(id)}?refundReason=${refundReason}`;
  return http.delete(url);
}

export function saveTaxCase(data: TaxCaseCreation) {
  return http.post<TaxCase>(apiEndpoint, data);
}
export function taxCaseBulkCreation(data: TaxCaseCreationBulk) {
  return http.post<{ data: string }>(`${apiEndpoint}/bulkCreate`, data);
}

export function saveTaxCaseStatus(id: IdType, data: CaseStatusUpdate) {
  return http.put<TaxCase>(`${taxCaseUrl(id)}/status`, data);
}

export function saveTaxCaseStatusToInProgress(id: IdType) {
  return http.put<TaxCase>(`${taxCaseUrl(id)}/status/inProgress`, {});
}

export function copyAllFromPreAssessment(id: IdType) {
  return http.put(
    `${taxCaseUrl(id)}/billingOptimization/copyAllFromPreAssessment`
  );
}

export function downloadAllFiles(id: IdType) {
  return http.get(`${taxCaseUrl(id)}/downloadAllFiles`);
}

export function updateTaxCaseClient(id: IdType, data: ChangeClientType) {
  return http.put(`${taxCaseUrl(id)}/client`, data);
}

export function saveTaxVehicle(id: IdType, data: Vehicle) {
  return http.put(`${taxCaseUrl(id)}/vehicle`, data);
}

export function saveOffer(id: IdType, data: any, optimizationMode?: boolean) {
  const dataKey = optimizationMode ? "internalCalculation" : "calculation";
  return http.put(
    `${taxCaseUrl(id)}/${optimizationMode ? "billingOptimization" : "preAssessment"
    }`,
    {
      [dataKey]: data,
      version: latestVersion,
    }
  );
}

export function sentTaxOffer(id: IdType, data: SendOfferFormType) {
  return http.post(`${taxCaseUrl(id)}/sendOffer`, data);
}

export function publishTaxOffer(id: IdType, data: any) {
  return http.put<TaxCase>(`${taxCaseUrl(id)}/published`, data);
}
export function toggleAcceptable(id: IdType, data: any) {
  return http.put<TaxCase>(`${taxCaseUrl(id)}/acceptable`, data);
}

export function followUpOnCase(id: IdType, data: any) {
  return http.put<TaxCase>(`${taxCaseUrl(id)}/followup`, data);
}

export function payWithQuickpay(id: IdType) {
  return http.post<{ data: LinkType }>(`${taxCaseUrl(id)}/payByCard`);
}

export function payWithStripe(id: IdType, { userId }: { userId?: IdType | null }) {
  return http.post<TaxCase>(`${taxCaseUrl(id)}/payBySavedCard`, { userId });
}

export function acceptTaxOffer(id: IdType, data: AcceptOfferFormType) {
  return http.put<TaxCase>(`${taxCaseUrl(id)}/accepted`, data);
}

export function sentTaxReceipt(id: IdType) {
  return http.put(`${taxCaseUrl(id)}/receipted`, { status: true });
}

export function checkValidMileage(id: IdType) {
  return http.post<TaxCase>(`${taxCaseUrl(id)}/checkValidMileage`);
}

// Invoice functions :

export function sentTaxInvoice(id: IdType, billingDestinationId: IdType) {
  return http.post(`${taxCaseUrl(id)}/createInvoice`, {
    draftOnly: false,
    send: true,
    billingDestinationId,
  });
}

export function saveTaxDraftInvoice(id: IdType, billingDestinationId: IdType) {
  return http.post(`${taxCaseUrl(id)}/createInvoice`, {
    draftOnly: true,
    send: false,
    billingDestinationId,
  });
}

export function sentTaxInvoiceAgain(id: IdType) {
  return http.post(`${taxCaseUrl(id)}/sendInvoice`);
}

export function paidTaxOffer(id: IdType, data: any) {
  return http.put(`${taxCaseUrl(id)}/paid`, data);
}

export function uploadedTaxOffer(id: IdType, data: any) {
  return http.put(`${taxCaseUrl(id)}/documented`, data);
}


export function saveTaxEquipments(
  id: IdType,
  data: any,
  optimizationMode?: boolean
) {
  return http.put(
    `${taxCaseUrl(id)}/${optimizationMode ? "billingOptimization" : "preAssessment"
    }/equipments`,
    data
  );
}

export function saveTradePriceCalculation(
  id: IdType,
  data: any,
  optimizationMode?: boolean
) {
  return http.patch(
    `${taxCaseUrl(id)}/${optimizationMode ? "billingOptimization" : "preAssessment"
    }/calculation`,
    data
  );
}

export function getTradePrice(
  id: IdType,
  data: any,
  optimizationMode?: boolean
) {
  return http.get(
    `${taxCaseUrl(id)}/${optimizationMode ? "billingOptimization" : "preAssessment"
    }/calculationOutput`,
    data
  );
}

export function downloadDocumentation(id: IdType, optimizationMode?: boolean) {
  return http.post(
    `${taxCaseUrl(id)}/${optimizationMode ? "billingOptimization" : "preAssessment"
    }/downloadDocumentation`,
    { advanced: true }
  );
}

export function downloadTaxOffer(taxCaseId: IdType, documentId: IdType) {
  return http.get<{ url: string }>(`${taxCaseUrl(taxCaseId)}}/downloadOffer/${documentId}`);
}

export function createLeasingofTaxCase(id: IdType, data: ToLeasingFormType) {
  return http.post(`${taxCaseUrl(id)}/toLeasingCase`, data);
}

export function createTaxDocofTaxCase(id: IdType) {
  return http.post(`${taxCaseUrl(id)}/toTaxDocCase`);
}

export function createImportOfTaxCase(id: IdType) {
  return http.post(`${taxCaseUrl(id)}/toImportCase`);
}

export function cloneTaxCase(id: IdType, data: CloneTaxCaseType) {
  return http.post<TaxCase>(`${taxCaseUrl(id)}/toTaxCase`, data);
}

export function taxCaseCreationRequest(data: TaxCreationRequestType) {
  return http.post(`${getApiEndpoint()}/taxCaseCreationRequest`, data);
}

export function taxCaseRecalculateRequest(id: IdType, data: TaxRecalculateRequestType) {
  return http.post(`${taxCaseUrl(id)}/askRecalculate`, data);
}


/**
 * Start the process for generating an automatic offer.
 * includes newPrice, tradePrice, adverts, registrationFee, newPriceManager and offer, registrationFee, newPriceManager and offer
 * */
export type AttemptAutoOfferOptions = { sendOffer?: boolean; allowInsecure?: boolean };

export function attemptAutoOffer(id: IdType, data: AttemptAutoOfferOptions) {
  return http.post(`${taxCaseUrl(id)}/attemptAutoOffer`, data);
}

export function checkMotorAgencyStatus(id: IdType) {
  return http.post(`${taxCaseUrl(id)}/checkMotorAgencyStatus`);
}

/**
 * @deprecated Documentation files are now scenario-based.
 * Use taxScenarioFileUrl from taxScenarioService instead.
 */
export function adsDataDocumentationFileEndpoint(id?: IdType) {
  return id ? `${taxCaseUrl(id)}/adsDataDocumentationFile` : mediaApiEndpoint;
}


export function rejectDocumentationCase(id: IdType, data: RejectCaseType) {
  return http.post(`${taxCaseUrl(id)}/close`, data);
}

export function purchaseByCard(id: IdType, data: PurchaseType) {
  return http.post<{ data: LinkType }>(
    `${taxCaseUrl(id)}/purchaseByCard`,
    data
  );
}

export function purchaseBySavedCard(id: IdType, data: PurchaseType) {
  return http.post<TaxCase>(`${taxCaseUrl(id)}/purchaseBySavedCard`, data);
}

export function taxAutoFetchDatDe(taxCaseId: IdType) {
  return http.post<TaxCase>(`${taxCaseUrl(taxCaseId)}/attemptAutoReportFetch`);
}

/**
 * You can test with this vehicle data
 *    'vin' => 'WBY41DU090SB99488',
 *    'first_reg_date' => "2022-01-01 13:00",
 *
 * POST taxCase/{taxCase}/fetchPriceDocumentation \
 * it works in the same way as /attemptAutoReportFetch .
 */
export function createEstimaticOfTaxCase(id: IdType) {
  return http.post(`${taxCaseUrl(id)}/initializeEstimaticCase`);
}

export function taxFetchPriceDocumentation(taxCaseId: IdType) {
  return http.post<TaxCase>(`${taxCaseUrl(taxCaseId)}/fetchPriceDocumentation`);
}
